import * as React from 'react';

export type ElementRect = {
  width: number;
  height: number;
  left: number;
  top: number;
  bottom: number;
  right: number;
};

export function useElementRect<T extends HTMLElement = HTMLDivElement>(
  onRectChange: (rect: ElementRect) => void,
) {
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const onRectChangeRef = React.useRef(onRectChange);

  React.useLayoutEffect(() => {
    onRectChangeRef.current = onRectChange;
  }, [onRectChange]);

  const setRef = React.useCallback((element: T | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    if (!element) return;

    const updateRect = () => {
      const rect = element.getBoundingClientRect();
      onRectChangeRef.current({
        width: rect.width,
        height: rect.height,
        left: rect.left,
        top: rect.top,
        bottom: rect.bottom,
        right: rect.right,
      });
    };

    updateRect();

    resizeObserverRef.current = new ResizeObserver(updateRect);
    resizeObserverRef.current.observe(element);
  }, []);

  React.useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
    },
    [],
  );

  return setRef;
}
